import PropTypes from "prop-types";
import { StarIcon } from "@phosphor-icons/react";
import CustomImage from "../../atoms/customImage";

const WARNING_STAR_SIZE = 16;

/**
 * @param {number} ratings
 * @param {number} stars
 * @param {number} starSize
 * @returns {import("react").ReactNode}
 */
function WarningRatingStars({ ratings, stars, starSize = WARNING_STAR_SIZE }) {
  const ratingNumberArray = ratings?.toString()?.split(".");
  const fullStarCount = ratingNumberArray?.[0] ? parseInt(ratingNumberArray[0], 10) : 0;
  const hasHalfStar = Boolean(ratingNumberArray?.[1] && parseInt(ratingNumberArray[1], 10) > 0);
  return (
    <div className="flex gap-[2px]">
      {Array.from({ length: stars }, (_, index) => {
        const isFilled = index < fullStarCount;
        const isHalf = !isFilled && hasHalfStar && index === fullStarCount;
        const weight = isFilled || isHalf ? "fill" : "regular";

        return <StarIcon key={index} size={starSize} weight={weight} className="text-warn-900" aria-hidden={true} />;
      })}
    </div>
  );
}

WarningRatingStars.propTypes = {
  ratings: PropTypes.number.isRequired,
  stars: PropTypes.number.isRequired,
  starSize: PropTypes.number,
};

/**
 * RatingStars component displays a rating in the form of stars.
 * @param {Object} props - Component props.
 * @param {number} props.ratings - The rating value.
 * @param {number} props.stars - The total number of stars to display.
 * @param {"default"|"warning"} props.variant - Star color theme.
 * @param {number} [props.starSize] - Pixel size for the warning variant.
 * @returns {JSX.Element} - Rendered component.
 */
const RatingStars = ({ ratings, stars, variant = "default", starSize }) => {
  if (!ratings && ratings !== 0) {
    return null;
  }

  if (variant === "warning") {
    return <WarningRatingStars ratings={ratings} stars={stars} starSize={starSize} />;
  }

  const ratingNumberArray = ratings?.toString()?.split(".");
  const fullStarCount = ratingNumberArray?.[0] ? parseInt(ratingNumberArray[0], 10) : 0;
  const emptyStarCount = stars - fullStarCount;
  let numOfStarsAdded = 0;

  return (
    <div className="flex gap-1">
      {Array.from({ length: fullStarCount }, (elem, index) => {
        numOfStarsAdded++;
        return (
          <span key={index}>
            <CustomImage src="/icons/rating-green.svg" alt="rating-star" width={15} height={15} sizes="10vw" />
          </span>
        );
      })}
      {ratingNumberArray?.[1] &&
        (() => {
          numOfStarsAdded++;
          return (
            <span>
              <CustomImage src="/icons/rating-green-half.svg" alt="half-star" width={15} height={15} sizes="10vw" />
            </span>
          );
        })()}
      {Array.from({ length: emptyStarCount }, (elem, index) => (
        <span key={index}>
          {numOfStarsAdded < stars && (
            <CustomImage src="/icons/empty-star.svg" alt="empty-star" width={15} height={15} sizes="10vw" />
          )}
        </span>
      ))}
    </div>
  );
};

RatingStars.propTypes = {
  ratings: PropTypes.number,
  stars: PropTypes.number.isRequired,
  variant: PropTypes.oneOf(["default", "warning"]),
  starSize: PropTypes.number,
};

export default RatingStars;
